import { _isEmpty, _isNotEmpty } from '@/littledash';
import { getAfterSaveAction } from '@/components/Workflow/Show/WorkflowTemplate.utils';
import { type WorkflowTabConfig } from '@/components/Workflow/Show/Workflow.model.ts';
import { type PresetCalculation } from '@/model/PresetCalculation.model';
import { consumptionMetricIds } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils';
import {
  PercentChangeMap,
  type WorkflowTemplateDefaults,
} from '@/components/Modals/SetupWorkflow/SetupWorkflow.model.ts';

export const WORKFLOW_SETUP_FORM_PATH = 'workflowSetupForm' as const;

const INTAKE_PATH = `${WORKFLOW_SETUP_FORM_PATH}.intake` as const;

export const WORKFLOW_INTAKE_FORM_PATH = {
  intake: INTAKE_PATH,
  foodEnabled: `${INTAKE_PATH}.foodEnabled`,
  fluidEnabled: `${INTAKE_PATH}.fluidEnabled`,
  timeEnabled: `${INTAKE_PATH}.timeEnabled`,
  order: `${INTAKE_PATH}.order`,
} as const;

export const WORKFLOW_TAB_INDEX = {
  MEASUREMENTS: 0,
  SAMPLES: 1,
  OBSERVATIONS: 2,
  DOSING: 3,
  CONSUMPTION: 4,
} as const;

export type WorkflowTabIndex = (typeof WORKFLOW_TAB_INDEX)[keyof typeof WORKFLOW_TAB_INDEX];

export const WORKFLOW_TAB_TYPE = {
  MEASUREMENTS: 'measurements',
  SAMPLES: 'samples',
  OBSERVATIONS: 'observations',
  DOSING: 'dosing',
  CONSUMPTION: 'consumption',
} as const;

export type WorkflowTabType = (typeof WORKFLOW_TAB_TYPE)[keyof typeof WORKFLOW_TAB_TYPE];

export type WorkflowTabPath<I extends WorkflowTabIndex = WorkflowTabIndex> = `workflowSetupForm.workflowTabConfig.${I}`;

export const workflowTabPath = <I extends WorkflowTabIndex>(tabIndex: I): WorkflowTabPath<I> =>
  `workflowSetupForm.workflowTabConfig.${tabIndex}`;

export type MeasurementChild = {
  id: string;
  name: string;
  displayName: string;
  value: boolean;
};

export const buildMeasurementChildren = (
  calculations: Array<PresetCalculation> = [],
  templateMeasurements: Array<string> = [],
  isMeasurementTemplate: boolean
): Array<MeasurementChild> => {
  // If coming from a measurement task execution with no measurements, ensure measurements are toggled off by default
  return calculations.reduce<Array<MeasurementChild>>((acc, { id, name, measurements }) => {
    if (!consumptionMetricIds.has(id) && _isNotEmpty(measurements)) {
      acc.push({
        id,
        name: id,
        displayName: name,
        value: _isNotEmpty(templateMeasurements) ? templateMeasurements.includes(id) : !isMeasurementTemplate,
      });
    }
    return acc;
  }, []);
};

export const assembleInitialWorkflow = (
  calculations: Array<PresetCalculation> = [],
  workflow: Array<WorkflowTabConfig>,
  templateDefaults: WorkflowTemplateDefaults | undefined,
  consumptionEnabled: boolean
) => {
  const isMeasurementTemplate = _isNotEmpty(templateDefaults?.measurements);
  const templateMeasurements = templateDefaults?.measurements?.measurements ?? [];
  const measurementChildren = buildMeasurementChildren(calculations, templateMeasurements, isMeasurementTemplate);

  if (_isNotEmpty(workflow)) {
    return workflow;
  }

  const workflowTabConfig: Array<WorkflowTabConfig> = [
    {
      name: 'measurements',
      displayName: 'Measurements',
      value: _isNotEmpty(calculations),
      canReorder: true,
      children: orderCalculations(measurementChildren, templateMeasurements),
      disabled: _isEmpty(calculations),
      percentChange: {
        weight: templateDefaults?.measurements?.weight_percentage_change ?? PercentChangeMap.fromFirstMeasurement,
      },
      altIdToAssign: templateDefaults?.measurements?.assign_identifier,
      onSave: getAfterSaveAction(templateDefaults?.measurements?.after_save_action),
      cursorPosition: templateDefaults?.measurements?.cursor_position,
    },
    {
      name: 'samples',
      displayName: 'Samples',
      value: true,
      autoGenerateSampleIds: templateDefaults?.samples?.auto_generate_sample_id ?? true,
      preSelectedSampleTypeIds: templateDefaults?.samples?.samples ?? [],
      children: [],
      onSave: getAfterSaveAction(templateDefaults?.samples?.after_save_action),
      disabled: false,
    },
    {
      name: 'observations',
      displayName: 'Observations',
      preSelectedObservationTypeIds: templateDefaults?.observations?.observations ?? [],
      value: true,
      onSave: getAfterSaveAction(templateDefaults?.observations?.after_save_action),
      disabled: false,
    },
    {
      name: 'dosing',
      displayName: 'Dosing',
      value: true,
      children: templateDefaults?.dosing?.treatments ?? [],
      onSave: getAfterSaveAction(templateDefaults?.dosing?.after_save_action),
      disabled: false,
      takeWeight: templateDefaults?.dosing?.use_new_body_weight ?? false,
      skipDosing: templateDefaults?.dosing?.skip_dosing_for_unrequired_animals ?? false,
    },
  ];

  if (consumptionEnabled) {
    workflowTabConfig.push({
      name: 'consumption',
      displayName: 'Cage Food and Fluid',
      value: true,
      children: [],
      onSave: 'focus-search',
      disabled: false,
    });
  }

  return workflowTabConfig;
};

export const assembleToggleOptions = (checks: Array<WorkflowTabConfig>, activeCheck: string) =>
  checks?.reduce<Record<string, { value: string; name: string }>>((acc, { value, name }) => {
    if (value && activeCheck !== name && name !== 'consumption') {
      acc[name] = {
        value: `toggle-${name}`,
        name: `Toggle ${name}`,
      };
    }

    return acc;
  }, {});

export const orderCalculations = (calculations: Array<MeasurementChild>, order: Array<string>) => {
  if (_isEmpty(order) || order.length < 2) {
    return calculations;
  }

  const orderMap = new Map(order.map((id, index) => [id, index]));
  const ordered: Array<MeasurementChild> = [];
  const unordered: Array<MeasurementChild> = [];

  calculations.forEach((calc) => {
    if (orderMap.has(calc.id)) {
      ordered.push(calc);
    } else {
      unordered.push(calc);
    }
  });

  // Sort based on the index of order;
  ordered.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));

  // Return ordered first then whatever the other measurements are on the preset.
  return [...ordered, ...unordered];
};
